// D1-backed alerts hook (S11-6) — consumes the S11-4 Cloud alerts API.
//
// Replaces the old agent-scoped useAlerts hook's role on the /alerts
// page. The old hook still works for pages that expect an agent-
// scoped array (kept for compatibility); this one is org-scoped,
// filter-driven, and returns the full envelope + cursor for
// pagination.
//
// Query keys:
//   ['alerts', 'list', filters]   — list query
//   ['alerts', 'count']            — sidebar badge (red-dot boolean)
//   ['alerts', 'recent-resolved'] — empty-state helper ("last resolved
//                                    <time> ago") — 24h window
//
// AppShell's real-time invalidation fires on alert.triggered /
// alert.resolved via `invalidateQueries({ queryKey: ['alerts'] })`
// which matches all three keys above.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { selectAlertsPage } from './selectAlertsPage.js';

const DEFAULT_FILTERS = { state: 'active', severity: null, agent_id: null, device_id: null };

/**
 * List alerts matching the current filter set. Response shape:
 *   { data: [...alerts], cursor: string|null, error: null }
 * select() unwraps the envelope to just the array — or, with
 * `withMeta`, to `{ items, truncated }`, where `truncated` says the server
 * had more rows than one page. A caller that COUNTS (the Overview) needs
 * that bit: the server pages severity-first then newest-first, so a
 * truncated page drops the oldest, lowest-severity alerts and a count built
 * on it is a floor, not a total (#998 review R3).
 */
export function useAlertsList(filters = DEFAULT_FILTERS, { limit = 100, withMeta = false } = {}) {
  const params = {};
  if (filters.state) params.state = filters.state;
  if (filters.severity) params.severity = filters.severity;
  if (filters.agent_id) params.agent_id = filters.agent_id;
  if (filters.device_id) params.device_id = filters.device_id;
  if (limit !== 100) params.limit = String(limit);

  return useQuery({
    queryKey: ['alerts', 'list', params],
    queryFn: () => api.get('/alerts', params),
    select: (resp) => selectAlertsPage(resp, withMeta),
  });
}

/**
 * How many active alerts? Used by the sidebar badge. Cheap query —
 * limit=1 gives us "is it >0?" which is all the badge needs. A
 * precise count requires a dedicated endpoint or full list walk.
 */
export function useActiveAlertCount() {
  return useQuery({
    queryKey: ['alerts', 'count'],
    // state=active is the default on the server but we send it
    // explicitly so cache keys don't collide with any other caller.
    queryFn: () => api.get('/alerts', { state: 'active', limit: '1' }),
    select: (resp) => {
      const items = resp?.data ?? resp;
      // hasMore = cursor is non-null → at least one more row beyond
      // the one we fetched. Total ≥1 if any items returned.
      const any = Array.isArray(items) && items.length > 0;
      return any;
    },
    // Badge should feel live — short stale-time so the first WS
    // invalidation hit re-fetches without delay.
    staleTime: 0,
  });
}

/**
 * Most-recently-resolved alert in the caller's org. Used for the
 * "Last alert resolved <relative time>" line on the otherwise-empty
 * active-alerts view.
 */
export function useMostRecentResolvedAlert() {
  return useQuery({
    queryKey: ['alerts', 'recent-resolved'],
    queryFn: () => api.get('/alerts', { state: 'resolved', limit: '1' }),
    select: (resp) => {
      const items = resp?.data ?? resp;
      return Array.isArray(items) && items.length > 0 ? items[0] : null;
    },
  });
}

/**
 * Acknowledge mutation with optimistic UI update + rollback on error.
 *
 * Optimistic strategy: flip the row to state='acknowledged' +
 * acknowledged_at=now across every cached list query that currently
 * contains it. On error, restore the snapshots. On success, the
 * response is the authoritative row which TanStack Query will refetch
 * on invalidate anyway.
 */
export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId) => api.post(`/alerts/${alertId}/acknowledge`, {}),
    onMutate: async (alertId) => {
      // Cancel outgoing list refetches so they don't overwrite our
      // optimistic update mid-flight.
      await qc.cancelQueries({ queryKey: ['alerts'] });
      // Snapshot every list-query cache so we can roll back on error.
      const snapshots = qc.getQueriesData({ queryKey: ['alerts', 'list'] });
      const now = new Date().toISOString();
      snapshots.forEach(([key]) => {
        qc.setQueryData(key, (old) => applyOptimistic(old, alertId, (a) => ({
          ...a,
          state: 'acknowledged',
          acknowledged_at: now,
        })));
      });
      return { snapshots };
    },
    onError: (_err, _alertId, ctx) => {
      ctx?.snapshots?.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => {
      // Regardless of success/failure: refetch so the UI reflects the
      // authoritative state (resolved-concurrently 409 path, etc.).
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

// vista#608: the per-alert operator-forced resolve mutation (`useResolveAlert`)
// was removed with the ack-only lifecycle — Acknowledge is the only manual
// per-alert action and the agent auto-resolves when the condition clears.

// vista#629: admin BULK "resolve all" — a maintenance tool to clear an alert
// backlog in one call (distinct from the per-alert lifecycle). Pass the current
// list filters ({ severity, agent_id, device_id }) as query params; the worker
// resolves all open (active + acknowledged) matches for the org. No optimistic
// update — a bulk change just invalidates and refetches the list + count.
export function useBulkResolveAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filters = {}) => {
      const params = {};
      if (filters.severity) params.severity = filters.severity;
      if (filters.agent_id) params.agent_id = filters.agent_id;
      if (filters.device_id) params.device_id = filters.device_id;
      return api.post('/alerts/resolve-all', null, params);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

// --- helpers --------------------------------------------------------------

// Apply a row-level update to the (possibly-enveloped) list cache
// without losing the envelope shape. Returns a new object so React
// Query detects the change.
function applyOptimistic(cachedResponse, alertId, updater) {
  if (!cachedResponse) return cachedResponse;
  // If the cached value is already an array (select() unwrapped),
  // update the element.
  if (Array.isArray(cachedResponse)) {
    return cachedResponse.map((a) => (a.alert_id === alertId ? updater(a) : a));
  }
  // Otherwise it's the envelope: {data, cursor, error}.
  if (Array.isArray(cachedResponse.data)) {
    return {
      ...cachedResponse,
      data: cachedResponse.data.map((a) => (a.alert_id === alertId ? updater(a) : a)),
    };
  }
  return cachedResponse;
}
